import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LoginForm from "./LoginForm";
import TwoFactorForm from "./TwoFactorForm";
import ChangePasswordForm from "./ChangePasswordForm";
import ForgotPasswordForm from "./ForgotPasswordForm";
import RegisterForm from "./RegisterForm";
import * as LoginService from "./LoginService";
import "./styles/styles.css";

const LoginPage = () => {
  const navigate = useNavigate();
  const { guid } = useParams();

  const [isMobile, setIsMobile] = useState(false);
  const [logoURL, setLogoURL] = useState(null);
  const [sideImageURL, setSideImageURL] = useState(null);
  const [clientDescription, setClientDescription] = useState(null);
  const [clientRegistrationInfo, setClientRegistrationInfo] = useState(null);

  const [loading, setLoading] = useState(true);

  const [currentForm, setCurrentForm] = useState("login"); // login | register | change | twoFactor | forgot
  const [emailForChange, setEmailForChange] = useState("");
  const [emailFor2FA, setEmailFor2FA] = useState("");
  const [passwordFor2FA, setPasswordFor2FA] = useState("");
  const [message, setMessage] = useState("");

  // Responsive
  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth <= 768);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Fetch client branding
  useEffect(() => {
    const fetchClient = async () => {
      try {
        const result = await LoginService.getClientInfo(guid);
        if (result) {
          setClientDescription(result.Description);
          setClientRegistrationInfo(result.ClientRegistrationInfo);
          setLogoURL(result.LoginLogoURL);
          setSideImageURL(result.LoginSideImageURL);
        }
      } catch (err) {
        console.error("Failed to fetch client info:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [guid]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const handleLoginSubmit = async (email, password) => {
    setMessage("");
    const result = await LoginService.login(guid, email, password);
    if (result === "2FA") {
      setEmailFor2FA(email);
      setPasswordFor2FA(password);
      setCurrentForm("twoFactor");
    } else if (result === "ChangePassword") {
      setEmailForChange(email);
      setCurrentForm("change");
    } else if (result === "Error") {
      setMessage("Information provided incorrect!");
    } else {
      localStorage.setItem("paxData", result);
      navigate(`/${guid}/dashboard`);
    }
  };

  const handleTwoFactorSubmit = async (code) => {
    setMessage("");

    const result = await LoginService.twoFactor(
      guid,
      emailFor2FA,
      passwordFor2FA,
      code,
    );

    if (result === "Error") {
      setMessage("Information provided incorrect!");
      return;
    }

    if (
      typeof result === "string" &&
      result.toLowerCase().includes("incorrect")
    ) {
      setMessage("Information provided incorrect!");
      return;
    }

    if (!result || typeof result !== "object") {
      setMessage("Unexpected login response.");
      return;
    }

    localStorage.setItem("paxData", JSON.stringify(result));
    navigate(`/${guid}/dashboard`);
  };

  const handleChangePasswordSubmit = async (oldPassword, newPassword) => {
    setMessage("");
    const result = await LoginService.updatePassword(
      guid,
      emailForChange,
      oldPassword,
      newPassword,
    );
    if (result === "Error") {
      setMessage("Information provided incorrect!");
    } else {
      setCurrentForm("login");
      setEmailForChange("");
      setMessage("Password updated successfully!");
    }
  };

  const handleLostPasswordSubmit = async (email) => {
    setMessage("");
    const result = await LoginService.forgotPassword(guid, email);
    if (result === "Error") {
      setMessage("Unable to reset password. Please check your email address.");
    } else {
      setMessage("Password reset instructions sent to your email.");
      setCurrentForm("login");
    }
  };

  const handleRegisterSubmit = async (email) => {
    setMessage("");
    const result = await LoginService.register(guid, email);
    setMessage(
      result.includes("Ok!")
        ? "Registration successful! An email with your new temporary password has been sent!"
        : result,
    );
    if (result.includes("Ok!")) setCurrentForm("login");
  };

  return (
    <div className="login-container">
      {!isMobile && (
        <div className="image-section">
          <img src={sideImageURL} alt="Visual" />

          <div className="image-footer">
            Powered by{" "}
            <a
              href="https://catss.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
            >
              CATSS
            </a>{" "}
            – Tested on Chrome &amp; Edge
          </div>
        </div>
      )}

      <div className="form-section">
        <div className="innerFormContainer">
          {currentForm === "login" && (
            <LoginForm
              onSubmit={handleLoginSubmit}
              onForgotPassword={() => setCurrentForm("forgot")}
              onRegister={() => setCurrentForm("register")}
              logoURL={logoURL}
              clientDescription={clientDescription}
              message={message}
              setMessage={setMessage}
            />
          )}
          {currentForm === "register" && (
            <RegisterForm
              onSubmit={handleRegisterSubmit}
              onBackToLogin={() => setCurrentForm("login")}
              logoURL={logoURL}
              clientDescription={clientDescription}
              clientRegistrationInfo={clientRegistrationInfo}
              message={message}
              setMessage={setMessage}
            />
          )}
          {currentForm === "forgot" && (
            <ForgotPasswordForm
              onSubmit={handleLostPasswordSubmit}
              onBackToLogin={() => setCurrentForm("login")}
              logoURL={logoURL}
              clientDescription={clientDescription}
              message={message}
              setMessage={setMessage}
            />
          )}
          {currentForm === "twoFactor" && (
            <TwoFactorForm
              onSubmit={handleTwoFactorSubmit}
              onBackToLogin={() => setCurrentForm("login")}
              logoURL={logoURL}
              clientDescription={clientDescription}
              message={message}
              setMessage={setMessage}
            />
          )}
          {currentForm === "change" && (
            <ChangePasswordForm
              onSubmit={handleChangePasswordSubmit}
              onBackToLogin={() => setCurrentForm("login")}
              logoURL={logoURL}
              clientDescription={clientDescription}
              message={message}
              setMessage={setMessage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
